import styled, { css, keyframes } from 'styled-components';
import { pxToRem } from 'styles/functions';
import type { ARWelcomeFrameAnimationType } from '../../../types/augmentedReality';

export const SCWelcomeContainer = styled.div`
  margin: ${pxToRem(20)};
`

export interface SCWelcomeFramedProps {
  $radius?: number;
  $borderWidth?: number;
  $animationType?: ARWelcomeFrameAnimationType;
}

export const SCWelcomeFramed = styled.div<SCWelcomeFramedProps>`
  --base: ${({ theme }) =>
    theme.ra_border_color || theme.photobooth_button_color || theme.button_color || theme.ra_border_color || '#34b076'};
  --lightest: color-mix(in srgb, var(--base), white 60%);
  --lighter: color-mix(in srgb, var(--base), white 30%);
  --darker: color-mix(in srgb, var(--base), black 30%);
  --darkest: color-mix(in srgb, var(--base), black 60%);
  --radius: ${({ theme, $radius }) =>
    theme.ra_border_radius ? theme.ra_border_radius : $radius === 50 ? '100%' : `${$radius || 0}px`};
  --border-width: ${({ $borderWidth }) => $borderWidth ?? 0}px;
  --border-out: ${pxToRem(-2)};
  --border-in: 4px;

  position: relative;
  isolation: isolate;
  width: 100%;
  border-radius: var(--radius);
  box-sizing: border-box;

  ${({ $animationType }) =>
    $animationType
      ? css`
          padding: var(--border-in);
          ${getAnimationStyles($animationType)}
        `
      : css`
          border: ${pxToRem(2)} solid var(--base);
        `}
`

export const SCWelcomeContent = styled.div`
  padding: ${pxToRem(10)};
`

export const SCWelcomeTitle = styled.h2`
  color: black;
  text-align: center;
`;

export const SCWelcomeImgContainer = styled.div`
  padding: ${pxToRem(10)};
`

interface SCWelcomeImgWrapperProps {
  $radius: number;
}
export const SCWelcomeImgWrapper = styled.div<SCWelcomeImgWrapperProps>`
  position: relative;
  overflow: hidden;
  border-radius: ${({ $radius }) => $radius === 50 ? '100%' : `${$radius}px`};
  line-height: 0;
`;

interface SCWelcomeRibbonProps {
  $visited: boolean;
}
export const SCWelcomeRibbon = styled.div<SCWelcomeRibbonProps>`
  display: ${({ $visited }) => ($visited ? 'block' : 'none')};
  position: absolute;
  top: ${pxToRem(18)};
  left: ${pxToRem(-32)};
  width: ${pxToRem(130)};
  padding: ${pxToRem(5)} 0;
  background-color: ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'};
  color: white;
  text-align: center;
  font-size: ${pxToRem(10)};
  font-weight: 700;
  line-height: 1.4;
  transform: rotate(-45deg);
  pointer-events: none;
  z-index: 1;
  ${({ theme }) =>
    theme.button_font &&
    css`
      font-family: ${theme.button_font};
    `}
`

interface SCWelcomeImgProps {
  $radius: number;
}
export const SCWelcomeImg = styled.img<SCWelcomeImgProps>`
  border-radius: ${({ $radius }) => $radius === 50 ? '100%' : `${$radius}px`};
  width: 100%;
  margin: auto;
`;

export const SCWelcomeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  word-break: break-all;
  background-color: ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'};
  color: white;
  padding: 0.75rem;
  width: 100%;
  border: var(--border-width) solid ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'};
  border-radius: ${({ theme }) => pxToRem(theme.button_radius || 0)};
  box-shadow: none;
  transition: 0.2s cubic-bezier(0.25, 0.1, 0.25, 0.1);
  cursor: pointer;

  &:hover {
    background-color: transparent;
    color: ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'};
  }

  label {
    display: flex;
    align-items: center;
    cursor: pointer;
    pointer-events: none;
    font-weight: 700;
    ${({ theme }) => theme.button_font && css`font-family: ${theme.button_font};`};
  }
`;

// --------------------------------------------------
// Animations styles
// --------------------------------------------------

const spinBorderAnimation = keyframes`
  from { --a: 0deg; }
  to { --a: 360deg; }
`;

const shimmerAnimation = keyframes`
  0% { transform: translateX(-200%) skewX(-45deg); }
  100% { transform: translateX(300%) skewX(-45deg); }
`;

const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 var(--lighter); }
  70% { box-shadow: 0 0 0 ${pxToRem(10)} transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`

const spinBorderStyles = css`
  &::before {
    content: '';
    position: absolute;
    inset: var(--border-out, ${pxToRem(-2)});
    border-radius: inherit;
    z-index: -1;
    background: conic-gradient(
      from var(--a, 0deg),
      var(--darkest),
      var(--darker),
      var(--base),
      var(--lighter),
      var(--lightest),
      var(--lighter),
      var(--base),
      var(--darker),
      var(--darkest)
    );
    padding: var(--border-width);
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    animation: ${spinBorderAnimation} 3s linear infinite;
  }
`

const shimmerStyles = css`
  overflow: hidden;
  border: var(--border-width) solid var(--base);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    z-index: 2;
    pointer-events: none;
    animation: ${shimmerAnimation} 2s infinite;
  }
`;

const pulseStyles = css`
  border: var(--border-width) solid var(--base);
  animation: ${pulseAnimation} 2s infinite;
`;

const rainbowStyles = css`
  &::before {
    content: '';
    position: absolute;
    inset: var(--border-out, ${pxToRem(-2)});
    border-radius: inherit;
    z-index: -1;
    background: conic-gradient(
      from var(--a, 0deg),
      hsl(4, 82%, 66%),
      hsl(11, 86%, 66%),
      hsl(40, 90%, 68%),
      hsl(161, 33%, 60%),
      hsl(208, 48%, 60%),
      hsl(283, 30%, 68%),
      hsl(4, 82%, 66%)
    );
    padding: var(--border-width);
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    animation: ${spinBorderAnimation} 3s linear infinite;
  }
`

const getAnimationStyles = (type?: string) => {
  switch (type) {
    case 'shimmer':
      return shimmerStyles;
    case 'pulse':
      return pulseStyles;
    case 'rainbow':
      return rainbowStyles;
    case 'spin-border':
      return spinBorderStyles;
    default:
      return css`
        border: var(--border-width) solid var(--base);
      `;
  }
};